import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CalendarClock, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = { pending: "bg-amber-500/15 text-amber-600", approved: "bg-emerald-500/15 text-emerald-600", rejected: "bg-destructive/15 text-destructive" };

export default function TimeOff() {
  const { t } = useT();
  const { isManager } = useCenter();
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", type: "vacation", reason: "" });

  const load = () => api.get("/schedule/vacations").then((r) => setRequests(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/schedule/vacations", form); toast.success(t("requestTimeOff")); setOpen(false); setForm({ start_date: "", end_date: "", type: "vacation", reason: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decide = async (id, status) => { await api.put(`/schedule/vacations/${id}`, { status }); load(); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("timeOff")}</h1>
        <Button onClick={() => setOpen(true)} data-testid="request-timeoff-button"><Plus className="w-4 h-4 mr-1.5" />{t("requestTimeOff")}</Button>
      </div>

      <div className="space-y-3">
        {requests.length === 0 && <p className="text-muted-foreground text-center py-12">{t("noData")}</p>}
        {requests.map((r) => (
          <Card key={r.id} className="p-4 flex items-center gap-3" data-testid="vacation-request">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><CalendarClock className="w-5 h-5 text-primary" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium capitalize">{t(r.type)}</p>
                {isManager && <span className="text-sm text-muted-foreground">· {r.employee_name}</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status]}`}>{t(r.status)}</span>
              </div>
              <p className="text-sm text-muted-foreground">{r.start_date} → {r.end_date}</p>
              {r.reason && <p className="text-sm text-muted-foreground mt-0.5">{r.reason}</p>}
            </div>
            {isManager && r.status === "pending" && (
              <div className="flex gap-1">
                <Button size="icon" variant="outline" onClick={() => decide(r.id, "approved")} data-testid="approve-request"><Check className="w-4 h-4 text-emerald-600" /></Button>
                <Button size="icon" variant="outline" onClick={() => decide(r.id, "rejected")} data-testid="reject-request"><X className="w-4 h-4 text-destructive" /></Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("requestTimeOff")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("reason")} — {t("vacation")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="timeoff-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="vacation">{t("vacation")}</SelectItem><SelectItem value="sick">{t("sick")}</SelectItem><SelectItem value="personal">{t("personal")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label className="text-xs">{t("startDate")}</Label><Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="timeoff-start" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("endDate")}</Label><Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="timeoff-end" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">{t("reason")}</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} disabled={!form.start_date || !form.end_date} data-testid="submit-timeoff">{t("confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
